import { useCallback, useMemo } from 'react';
import { InteractiveTrackMap } from '@/components/common/trackmap/InteractiveTrackMap';
import { localMetersToGeo, calculateZoomLevel } from '@/lib/map/mapGeometry';
import type { Point2D } from '@/lib/map/mapGeometry';

interface TrackSatelliteImageProps {
  latitude: number;
  longitude: number;
  bboxMinLat?: number;
  bboxMaxLat?: number;
  bboxMinLng?: number;
  bboxMaxLng?: number;
  viewLat?: number;
  viewLng?: number;
  viewZoom?: number;
  onViewChange?: (lat: number, lng: number, zoom: number) => void;
}

export function TrackSatelliteImage({
  latitude,
  longitude,
  bboxMinLat,
  bboxMaxLat,
  bboxMinLng,
  bboxMaxLng,
  onViewChange,
}: TrackSatelliteImageProps) {
  const hasCoords = latitude !== 0 || longitude !== 0;
  const hasBbox = !!(bboxMinLat && bboxMaxLat && bboxMinLng && bboxMaxLng);

  const anchor = useMemo(() => {
    if (hasBbox) {
      return {
        lat: (bboxMinLat! + bboxMaxLat!) / 2,
        lng: (bboxMinLng! + bboxMaxLng!) / 2,
      };
    }
    return { lat: latitude, lng: longitude };
  }, [hasBbox, bboxMinLat, bboxMaxLat, bboxMinLng, bboxMaxLng, latitude, longitude]);

  const referencePoints = useMemo(() => {
    if (hasBbox) {
      return [
        { lat: bboxMinLat!, lng: bboxMinLng! },
        { lat: bboxMaxLat!, lng: bboxMaxLng! },
      ];
    }
    return [{ lat: latitude, lng: longitude }];
  }, [hasBbox, bboxMinLat, bboxMaxLat, bboxMinLng, bboxMaxLng, latitude, longitude]);

  const handleViewportChange = useCallback(
    (viewport: { center: Point2D; scale: number }) => {
      if (!onViewChange) return;
      const centerGeo = localMetersToGeo(viewport.center, anchor);
      const zoom = calculateZoomLevel(viewport.scale, centerGeo.lat);
      onViewChange(
        Math.round(centerGeo.lat * 1e6) / 1e6,
        Math.round(centerGeo.lng * 1e6) / 1e6,
        Math.round(zoom * 100) / 100
      );
    },
    [anchor, onViewChange]
  );

  if (!hasCoords) {
    return null;
  }

  return (
    <div className="mb-6 w-full lg:max-w-2xl mx-auto relative">
      {/* Map container */}
      <div className="rounded-2xl shadow-2xl border border-white/10 overflow-hidden relative isolate aspect-square">
        <InteractiveTrackMap
          anchor={anchor}
          points={referencePoints}
          onViewportChange={handleViewportChange}
          className="w-full h-full"
        />
      </div>
    </div>
  );
}
